import React, { type ComponentType } from 'react';
import '@atlaskit/css-reset';
import type { EntryPointProps } from 'react-relay';
import AppBase from '@atlassian/jira-app-base';
import ProjectContextProvider from '@atlassian/jira-business-entity-project-provider/src/ui/project-context/index.tsx';
import ListView from '@atlassian/jira-business-list';
import { ListFeaturesProvider } from '@atlassian/jira-business-list/src/controllers/features-context';
import { LIST_VIEW_PREFERENCES } from '@atlassian/jira-business-preferences/src/constants.tsx';
import { ViewPreferencesProvider } from '@atlassian/jira-business-preferences/src/controllers/view-preferences-context/index.tsx';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import type { formatRulesQuery as FormatRulesQuery } from '@atlassian/jira-relay/src/__generated__/formatRulesQuery.graphql';
import { useIsSubtasksEnabled } from '@atlassian/jira-router-resources-business-configuration';
import { useProject } from '@atlassian/jira-router-resources-business-project-details';
import { SubProductUpdater } from '@atlassian/jira-spa-apps-common/src/analytics-sub-product/sub-product-updater/index.tsx';
import { AuthenticationCheck } from '@atlassian/jira-spa-apps-common/src/interceptors/authentication';
import UFOSegment from '@atlassian/jira-ufo-segment';

export type JiraBusinessListAppType = EntryPointProps<
	{ formattingRulesQuery: FormatRulesQuery },
	{},
	{},
	{}
>;

const JiraBusinessListApp: ComponentType<JiraBusinessListAppType> = (props) => {
	const { data: project } = useProject();
	const isSubtasksEnabled = useIsSubtasksEnabled();
	const isSimplifiedProject = Boolean(project?.isSimplified);
	const canCreateIssues = Boolean(project?.permissions.createIssues);
	const canCreateSubtask = canCreateIssues && (isSimplifiedProject || isSubtasksEnabled);

	return (
		<UFOSegment name="business-list-app">
			<AuthenticationCheck>
				<AppBase id="business">
					<ProjectContextProvider>
						<ListFeaturesProvider
							hasCreateSubtask={canCreateSubtask}
							canCreateIssues={canCreateIssues}
						>
							<ViewPreferencesProvider view="list" preferences={LIST_VIEW_PREFERENCES}>
								<ListView
									formattingRulesQuery={
										expValEquals('list_view_conditional_formatting', 'cohort', 'variation')
											? props.queries.formattingRulesQuery
											: null
									}
								/>
							</ViewPreferencesProvider>
						</ListFeaturesProvider>
					</ProjectContextProvider>
					<SubProductUpdater subProduct="core" />
				</AppBase>
			</AuthenticationCheck>
		</UFOSegment>
	);
};

export default JiraBusinessListApp;
